<template>
  <div class="app-container toolbox-page">
    <div class="toolbox-layout">
      <aside class="toolbox-nav">
        <div class="toolbox-nav-title">
          <span class="toolbox-title-symbol" aria-hidden="true">
            <span v-for="dot in 9" :key="dot" />
          </span>
          <span>工具箱</span>
        </div>
        <div
          v-for="item in tools"
          :key="item.key"
          class="toolbox-nav-item"
          :class="{ active: item.key === activeTool }"
          @click="activeTool = item.key"
        >
          <i :class="item.icon" />
          <div>
            <strong>{{ item.title }}</strong>
            <span>{{ item.subTitle }}</span>
          </div>
        </div>
      </aside>

      <section class="toolbox-content">
        <div class="tool-header">
          <div>
            <h2>税率计算</h2>
            <p>含税金额、税额拆分、付款节点金额与人民币大写</p>
          </div>
          <div class="tool-actions">
            <el-button icon="el-icon-refresh-left" @click="resetForm">重置</el-button>
            <el-button icon="el-icon-download" @click="exportExcel">导出Excel</el-button>
            <el-button type="primary" icon="el-icon-document-copy" @click="copyResult">复制结果</el-button>
          </div>
        </div>

        <el-form ref="calculatorForm" :model="form" label-width="118px" class="calculator-form">
          <el-row :gutter="14">
            <el-col :xs="24" :sm="12" :lg="8">
              <el-form-item label="含税总金额">
                <el-input-number
                  v-model="form.taxIncludedAmount"
                  :min="0"
                  :precision="2"
                  :step="1000"
                  controls-position="right"
                />
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12" :lg="8">
              <el-form-item label="增值税税率">
                <el-input-number
                  v-model="form.taxRate"
                  :min="0"
                  :max="100"
                  :precision="2"
                  :step="1"
                  controls-position="right"
                />
                <span class="input-suffix">%</span>
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12" :lg="8">
              <el-form-item label="付款比例合计">
                <span class="rate-total" :class="{ warning: paymentRateTotal !== 100 }">
                  {{ formatPercent(paymentRateTotal) }}
                </span>
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12" :lg="8">
              <el-form-item label="首付款比例">
                <el-input-number
                  v-model="form.firstPayRate"
                  :min="0"
                  :max="100"
                  :precision="2"
                  :step="5"
                  controls-position="right"
                />
                <span class="input-suffix">%</span>
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12" :lg="8">
              <el-form-item label="二次付款比例">
                <el-input-number
                  v-model="form.secondPayRate"
                  :min="0"
                  :max="100"
                  :precision="2"
                  :step="5"
                  controls-position="right"
                />
                <span class="input-suffix">%</span>
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12" :lg="8">
              <el-form-item label="三次付款比例">
                <el-input-number
                  v-model="form.thirdPayRate"
                  :min="0"
                  :max="100"
                  :precision="2"
                  :step="5"
                  controls-position="right"
                />
                <span class="input-suffix">%</span>
              </el-form-item>
            </el-col>
          </el-row>
        </el-form>

        <div class="summary-strip">
          <div class="summary-item">
            <span>不含税金额</span>
            <strong>{{ formatMoney(netAmount) }}</strong>
          </div>
          <div class="summary-item">
            <span>增值税额</span>
            <strong>{{ formatMoney(taxAmount) }}</strong>
          </div>
          <div class="summary-item">
            <span>付款总计</span>
            <strong>{{ formatMoney(paymentTotal) }}</strong>
          </div>
          <div class="summary-item">
            <span>差额</span>
            <strong :class="{ warning: paymentDiff !== 0 }">{{ formatMoney(paymentDiff) }}</strong>
          </div>
        </div>

        <el-table :data="resultRows" border size="small" class="result-table">
          <el-table-column prop="label" label="项目" min-width="160" />
          <el-table-column prop="basis" label="计算口径" min-width="210" />
          <el-table-column label="金额" width="150" align="right">
            <template slot-scope="scope">{{ formatMoney(scope.row.amount) }}</template>
          </el-table-column>
          <el-table-column prop="upper" label="大写金额" min-width="260" show-overflow-tooltip />
        </el-table>

        <el-alert
          v-if="paymentRateTotal !== 100"
          class="payment-warning"
          title="付款比例合计未等于100%，付款总计会与含税总金额产生差额。"
          type="warning"
          :closable="false"
          show-icon
        />
      </section>
    </div>
  </div>
</template>

<script>
const defaultForm = {
  taxIncludedAmount: 33750,
  taxRate: 13,
  firstPayRate: 50,
  secondPayRate: 50,
  thirdPayRate: 0
}

const upperDigits = ['零', '壹', '贰', '叁', '肆', '伍', '陆', '柒', '捌', '玖']
const smallUnits = ['仟', '佰', '拾', '']
const sectionUnits = ['', '万', '亿', '兆']
const crcTable = Array.from({ length: 256 }, (item, index) => {
  let crc = index
  for (let bit = 0; bit < 8; bit++) {
    crc = crc & 1 ? 0xedb88320 ^ (crc >>> 1) : crc >>> 1
  }
  return crc >>> 0
})

export default {
  name: 'ToolboxInvoiceTax',
  data() {
    return {
      activeTool: 'invoiceTax',
      tools: [
        {
          key: 'invoiceTax',
          title: '税率计算',
          subTitle: '税额拆分、付款比例、大写金额',
          icon: 'el-icon-document'
        }
      ],
      form: { ...defaultForm }
    }
  },
  computed: {
    taxIncludedAmount() {
      return this.toNumber(this.form.taxIncludedAmount)
    },
    taxRate() {
      return this.toNumber(this.form.taxRate)
    },
    netAmount() {
      if (!this.taxIncludedAmount) {
        return 0
      }
      return this.roundMoney(this.taxIncludedAmount / (1 + this.taxRate / 100))
    },
    taxAmount() {
      return this.roundMoney(this.taxIncludedAmount - this.netAmount)
    },
    firstPayAmount() {
      return this.roundMoney(this.taxIncludedAmount * this.toNumber(this.form.firstPayRate) / 100)
    },
    secondPayAmount() {
      return this.roundMoney(this.taxIncludedAmount * this.toNumber(this.form.secondPayRate) / 100)
    },
    thirdPayAmount() {
      return this.roundMoney(this.taxIncludedAmount * this.toNumber(this.form.thirdPayRate) / 100)
    },
    paymentRateTotal() {
      return this.roundRate(
        this.toNumber(this.form.firstPayRate) +
        this.toNumber(this.form.secondPayRate) +
        this.toNumber(this.form.thirdPayRate)
      )
    },
    paymentTotal() {
      return this.roundMoney(this.firstPayAmount + this.secondPayAmount + this.thirdPayAmount)
    },
    paymentDiff() {
      return this.roundMoney(this.paymentTotal - this.taxIncludedAmount)
    },
    resultRows() {
      return [
        {
          label: '含税总金额',
          basis: '手工录入',
          amount: this.taxIncludedAmount,
          upper: this.toRmbUpper(this.taxIncludedAmount)
        },
        {
          label: '不含税金额',
          basis: '含税总金额 / (1 + 税率)',
          amount: this.netAmount,
          upper: this.toRmbUpper(this.netAmount)
        },
        {
          label: '增值税额',
          basis: '含税总金额 - 不含税金额',
          amount: this.taxAmount,
          upper: this.toRmbUpper(this.taxAmount)
        },
        {
          label: '首付款金额',
          basis: `含税总金额 x ${this.formatPercent(this.form.firstPayRate)}`,
          amount: this.firstPayAmount,
          upper: this.toRmbUpper(this.firstPayAmount)
        },
        {
          label: '二次款金额',
          basis: `含税总金额 x ${this.formatPercent(this.form.secondPayRate)}`,
          amount: this.secondPayAmount,
          upper: this.toRmbUpper(this.secondPayAmount)
        },
        {
          label: '三次款金额',
          basis: `含税总金额 x ${this.formatPercent(this.form.thirdPayRate)}`,
          amount: this.thirdPayAmount,
          upper: this.toRmbUpper(this.thirdPayAmount)
        },
        {
          label: '付款总计',
          basis: '三次付款金额合计',
          amount: this.paymentTotal,
          upper: this.toRmbUpper(this.paymentTotal)
        }
      ]
    }
  },
  methods: {
    toNumber(value) {
      const number = Number(value)
      return Number.isFinite(number) ? number : 0
    },
    roundMoney(value) {
      return Math.round((this.toNumber(value) + Number.EPSILON) * 100) / 100
    },
    roundRate(value) {
      return Math.round((this.toNumber(value) + Number.EPSILON) * 100) / 100
    },
    formatMoney(value) {
      const number = this.roundMoney(value)
      return `￥${number.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    },
    formatPercent(value) {
      const number = this.roundRate(value)
      return `${number.toLocaleString('zh-CN', { maximumFractionDigits: 2 })}%`
    },
    resetForm() {
      this.form = { ...defaultForm }
    },
    exportExcel() {
      const files = this.createWorkbookFiles()
      const blob = new Blob([this.createZip(files)], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `税率计算_${this.timestamp()}.xlsx`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    },
    copyResult() {
      const content = [
        `含税总金额：${this.formatMoney(this.taxIncludedAmount)}，${this.toRmbUpper(this.taxIncludedAmount)}`,
        `增值税税率：${this.formatPercent(this.taxRate)}`,
        `不含税金额：${this.formatMoney(this.netAmount)}，${this.toRmbUpper(this.netAmount)}`,
        `增值税额：${this.formatMoney(this.taxAmount)}，${this.toRmbUpper(this.taxAmount)}`,
        `首付款金额：${this.formatMoney(this.firstPayAmount)}，${this.toRmbUpper(this.firstPayAmount)}`,
        `二次款金额：${this.formatMoney(this.secondPayAmount)}，${this.toRmbUpper(this.secondPayAmount)}`,
        `三次款金额：${this.formatMoney(this.thirdPayAmount)}，${this.toRmbUpper(this.thirdPayAmount)}`,
        `付款总计：${this.formatMoney(this.paymentTotal)}，${this.toRmbUpper(this.paymentTotal)}`
      ].join('\n')
      const input = document.createElement('textarea')
      input.value = content
      input.setAttribute('readonly', 'readonly')
      input.style.position = 'absolute'
      input.style.left = '-9999px'
      document.body.appendChild(input)
      input.select()
      document.execCommand('copy')
      document.body.removeChild(input)
      this.$message.success('已复制计算结果')
    },
    createWorkbookFiles() {
      return [
        { name: '[Content_Types].xml', content: this.contentTypesXml() },
        { name: '_rels/.rels', content: this.packageRelsXml() },
        { name: 'docProps/app.xml', content: this.appPropsXml() },
        { name: 'docProps/core.xml', content: this.corePropsXml() },
        { name: 'xl/workbook.xml', content: this.workbookXml() },
        { name: 'xl/_rels/workbook.xml.rels', content: this.workbookRelsXml() },
        { name: 'xl/styles.xml', content: this.stylesXml() },
        { name: 'xl/worksheets/sheet1.xml', content: this.sheetXml() }
      ]
    },
    contentTypesXml() {
      return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/docProps/app.xml" ContentType="application/vnd.openxmlformats-officedocument.extended-properties+xml"/>
  <Override PartName="/docProps/core.xml" ContentType="application/vnd.openxmlformats-package.core-properties+xml"/>
  <Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
  <Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
  <Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
</Types>`
    },
    packageRelsXml() {
      return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
  <Relationship Id="rId2" Type="http://schemas.openxmlformats.org/package/2006/relationships/metadata/core-properties" Target="docProps/core.xml"/>
  <Relationship Id="rId3" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/extended-properties" Target="docProps/app.xml"/>
</Relationships>`
    },
    appPropsXml() {
      return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Properties xmlns="http://schemas.openxmlformats.org/officeDocument/2006/extended-properties" xmlns:vt="http://schemas.openxmlformats.org/officeDocument/2006/docPropsVTypes">
  <Application>XCRM</Application>
  <HeadingPairs>
    <vt:vector size="2" baseType="variant">
      <vt:variant><vt:lpstr>工作表</vt:lpstr></vt:variant>
      <vt:variant><vt:i4>1</vt:i4></vt:variant>
    </vt:vector>
  </HeadingPairs>
  <TitlesOfParts>
    <vt:vector size="1" baseType="lpstr">
      <vt:lpstr>税率计算</vt:lpstr>
    </vt:vector>
  </TitlesOfParts>
</Properties>`
    },
    corePropsXml() {
      const now = new Date().toISOString()
      return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<cp:coreProperties xmlns:cp="http://schemas.openxmlformats.org/package/2006/metadata/core-properties" xmlns:dc="http://purl.org/dc/elements/1.1/" xmlns:dcterms="http://purl.org/dc/terms/" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">
  <dc:creator>XCRM</dc:creator>
  <cp:lastModifiedBy>XCRM</cp:lastModifiedBy>
  <dcterms:created xsi:type="dcterms:W3CDTF">${now}</dcterms:created>
  <dcterms:modified xsi:type="dcterms:W3CDTF">${now}</dcterms:modified>
</cp:coreProperties>`
    },
    workbookXml() {
      return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <sheets>
    <sheet name="税率计算" sheetId="1" r:id="rId1"/>
  </sheets>
  <calcPr calcId="191029" fullCalcOnLoad="1"/>
</workbook>`
    },
    workbookRelsXml() {
      return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>
  <Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`
    },
    stylesXml() {
      return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <numFmts count="3">
    <numFmt numFmtId="176" formatCode="[DBNum2][$RMB]General;[Red][DBNum2][$RMB]General"/>
    <numFmt numFmtId="177" formatCode="0.00_ "/>
    <numFmt numFmtId="178" formatCode="0.##%"/>
  </numFmts>
  <fonts count="4">
    <font><sz val="11"/><color rgb="FF000000"/><name val="宋体"/></font>
    <font><sz val="20"/><color rgb="FF000000"/><name val="宋体"/></font>
    <font><sz val="14"/><color rgb="FF000000"/><name val="宋体"/></font>
    <font><sz val="12"/><color rgb="FFFF0000"/><name val="宋体"/></font>
  </fonts>
  <fills count="3">
    <fill><patternFill patternType="none"/></fill>
    <fill><patternFill patternType="gray125"/></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FFFFFF00"/><bgColor indexed="64"/></patternFill></fill>
  </fills>
  <borders count="2">
    <border><left/><right/><top/><bottom/><diagonal/></border>
    <border>
      <left style="thin"><color rgb="FF000000"/></left>
      <right style="thin"><color rgb="FF000000"/></right>
      <top style="thin"><color rgb="FF000000"/></top>
      <bottom style="thin"><color rgb="FF000000"/></bottom>
      <diagonal/>
    </border>
  </borders>
  <cellStyleXfs count="1">
    <xf numFmtId="0" fontId="0" fillId="0" borderId="0"/>
  </cellStyleXfs>
  <cellXfs count="9">
    <xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"><alignment vertical="center"/></xf>
    <xf numFmtId="0" fontId="1" fillId="0" borderId="1" xfId="0" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
    <xf numFmtId="0" fontId="2" fillId="0" borderId="1" xfId="0" applyAlignment="1" applyBorder="1"><alignment horizontal="left" vertical="center"/></xf>
    <xf numFmtId="0" fontId="2" fillId="2" borderId="1" xfId="0" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="right" vertical="center"/></xf>
    <xf numFmtId="176" fontId="2" fillId="0" borderId="1" xfId="0" applyNumberFormat="1" applyBorder="1" applyAlignment="1"><alignment horizontal="left" vertical="center"/></xf>
    <xf numFmtId="177" fontId="2" fillId="0" borderId="1" xfId="0" applyNumberFormat="1" applyBorder="1" applyAlignment="1"><alignment horizontal="right" vertical="center"/></xf>
    <xf numFmtId="178" fontId="2" fillId="2" borderId="1" xfId="0" applyNumberFormat="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="right" vertical="center"/></xf>
    <xf numFmtId="0" fontId="2" fillId="0" borderId="1" xfId="0" applyBorder="1" applyAlignment="1"><alignment horizontal="right" vertical="center"/></xf>
    <xf numFmtId="0" fontId="3" fillId="0" borderId="0" xfId="0" applyAlignment="1"><alignment horizontal="left" vertical="center"/></xf>
  </cellXfs>
  <cellStyles count="1">
    <cellStyle name="Normal" xfId="0" builtinId="0"/>
  </cellStyles>
</styleSheet>`
    },
    sheetXml() {
      const firstRate = this.toNumber(this.form.firstPayRate) / 100
      const secondRate = this.toNumber(this.form.secondPayRate) / 100
      const thirdRate = this.toNumber(this.form.thirdPayRate) / 100
      return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <dimension ref="A1:C12"/>
  <sheetViews><sheetView workbookViewId="0"/></sheetViews>
  <sheetFormatPr defaultRowHeight="28"/>
  <cols>
    <col min="1" max="1" width="57.3" customWidth="1"/>
    <col min="2" max="2" width="17.3" customWidth="1"/>
    <col min="3" max="3" width="46.2" customWidth="1"/>
  </cols>
  <sheetData>
    ${this.excelRow(1, [this.textCell('A1', '税 率 计 算', 1), this.blankCell('B1', 1), this.blankCell('C1', 1)], 42)}
    ${this.excelRow(2, [this.textCell('A2', '含税总金额', 2), this.numberCell('B2', this.taxIncludedAmount, 3), this.formulaCell('C2', 'B2', this.taxIncludedAmount, 4)])}
    ${this.excelRow(3, [this.textCell('A3', '增值税税率（比如13%税率此处填13）', 2), this.numberCell('B3', this.taxRate, 3), this.blankCell('C3', 2)])}
    ${this.excelRow(4, [this.textCell('A4', '不含税金额', 2), this.formulaCell('B4', 'ROUND(B2/(1+B3/100),2)', this.netAmount, 5), this.formulaCell('C4', 'B4', this.netAmount, 4)])}
    ${this.excelRow(5, [this.textCell('A5', '增值税额', 2), this.formulaCell('B5', 'B2-B4', this.taxAmount, 5), this.formulaCell('C5', 'B5', this.taxAmount, 4)])}
    ${this.excelRow(6, [this.textCell('A6', '首付款付款比例（比如30%税率此处填30）', 2), this.numberCell('B6', firstRate, 6), this.blankCell('C6', 2)])}
    ${this.excelRow(7, [this.textCell('A7', '首付款金额', 2), this.formulaCell('B7', 'B2*B6', this.firstPayAmount, 7), this.formulaCell('C7', 'B7', this.firstPayAmount, 4)])}
    ${this.excelRow(8, [this.textCell('A8', '二次付款比例（比如30%税率此处填30）', 2), this.numberCell('B8', secondRate, 6), this.blankCell('C8', 2)])}
    ${this.excelRow(9, [this.textCell('A9', '二次款金额', 2), this.formulaCell('B9', 'B2*B8', this.secondPayAmount, 7), this.formulaCell('C9', 'B9', this.secondPayAmount, 4)])}
    ${this.excelRow(10, [this.textCell('A10', '三次付款比例（比如30%税率此处填30）', 2), this.numberCell('B10', thirdRate, 6), this.blankCell('C10', 2)])}
    ${this.excelRow(11, [this.textCell('A11', '三次款金额', 2), this.formulaCell('B11', 'B2*B10', this.thirdPayAmount, 7), this.formulaCell('C11', 'B11', this.thirdPayAmount, 4)])}
    ${this.excelRow(12, [this.textCell('A12', '付款总计（核对含税总金额）', 2), this.formulaCell('B12', 'B7+B9+B11', this.paymentTotal, 7), this.formulaCell('C12', 'B12', this.paymentTotal, 4)])}
  </sheetData>
  <mergeCells count="1">
    <mergeCell ref="A1:C1"/>
  </mergeCells>
  <pageMargins left="0.7" right="0.7" top="0.75" bottom="0.75" header="0.3" footer="0.3"/>
</worksheet>`
    },
    excelRow(rowIndex, cells, height) {
      const heightAttr = height ? ` ht="${height}" customHeight="1"` : ''
      return `<row r="${rowIndex}"${heightAttr}>${cells.join('')}</row>`
    },
    textCell(ref, value, styleId) {
      return `<c r="${ref}" s="${styleId}" t="inlineStr"><is><t>${this.escapeXml(value)}</t></is></c>`
    },
    blankCell(ref, styleId) {
      return `<c r="${ref}" s="${styleId}"/>`
    },
    numberCell(ref, value, styleId) {
      return `<c r="${ref}" s="${styleId}"><v>${this.excelNumber(value)}</v></c>`
    },
    formulaCell(ref, formula, value, styleId) {
      return `<c r="${ref}" s="${styleId}"><f>${this.escapeXml(formula)}</f><v>${this.excelNumber(value)}</v></c>`
    },
    excelNumber(value) {
      const number = this.toNumber(value)
      return Number.isInteger(number) ? String(number) : String(Number(number.toFixed(10)))
    },
    escapeXml(value) {
      return String(value).replace(/[<>&'"]/g, char => {
        const map = {
          '<': '&lt;',
          '>': '&gt;',
          '&': '&amp;',
          "'": '&apos;',
          '"': '&quot;'
        }
        return map[char]
      })
    },
    createZip(files) {
      const encoder = new TextEncoder()
      const localParts = []
      const centralParts = []
      let offset = 0
      files.forEach(file => {
        const nameBytes = encoder.encode(file.name)
        const dataBytes = encoder.encode(file.content)
        const crc = this.crc32(dataBytes)
        const localHeader = this.zipLocalHeader(nameBytes, dataBytes, crc)
        localParts.push(localHeader, dataBytes)
        centralParts.push(this.zipCentralHeader(nameBytes, dataBytes, crc, offset))
        offset += localHeader.length + dataBytes.length
      })
      const centralSize = centralParts.reduce((sum, part) => sum + part.length, 0)
      const endRecord = this.zipEndRecord(files.length, centralSize, offset)
      return this.concatBytes([...localParts, ...centralParts, endRecord])
    },
    zipLocalHeader(nameBytes, dataBytes, crc) {
      const header = new Uint8Array(30 + nameBytes.length)
      const view = new DataView(header.buffer)
      view.setUint32(0, 0x04034b50, true)
      view.setUint16(4, 20, true)
      view.setUint16(6, 0x0800, true)
      view.setUint16(8, 0, true)
      view.setUint16(10, 0, true)
      view.setUint16(12, 0, true)
      view.setUint32(14, crc, true)
      view.setUint32(18, dataBytes.length, true)
      view.setUint32(22, dataBytes.length, true)
      view.setUint16(26, nameBytes.length, true)
      view.setUint16(28, 0, true)
      header.set(nameBytes, 30)
      return header
    },
    zipCentralHeader(nameBytes, dataBytes, crc, offset) {
      const header = new Uint8Array(46 + nameBytes.length)
      const view = new DataView(header.buffer)
      view.setUint32(0, 0x02014b50, true)
      view.setUint16(4, 20, true)
      view.setUint16(6, 20, true)
      view.setUint16(8, 0x0800, true)
      view.setUint16(10, 0, true)
      view.setUint16(12, 0, true)
      view.setUint16(14, 0, true)
      view.setUint32(16, crc, true)
      view.setUint32(20, dataBytes.length, true)
      view.setUint32(24, dataBytes.length, true)
      view.setUint16(28, nameBytes.length, true)
      view.setUint16(30, 0, true)
      view.setUint16(32, 0, true)
      view.setUint16(34, 0, true)
      view.setUint16(36, 0, true)
      view.setUint32(38, 0, true)
      view.setUint32(42, offset, true)
      header.set(nameBytes, 46)
      return header
    },
    zipEndRecord(fileCount, centralSize, centralOffset) {
      const record = new Uint8Array(22)
      const view = new DataView(record.buffer)
      view.setUint32(0, 0x06054b50, true)
      view.setUint16(4, 0, true)
      view.setUint16(6, 0, true)
      view.setUint16(8, fileCount, true)
      view.setUint16(10, fileCount, true)
      view.setUint32(12, centralSize, true)
      view.setUint32(16, centralOffset, true)
      view.setUint16(20, 0, true)
      return record
    },
    concatBytes(parts) {
      const total = parts.reduce((sum, part) => sum + part.length, 0)
      const result = new Uint8Array(total)
      let offset = 0
      parts.forEach(part => {
        result.set(part, offset)
        offset += part.length
      })
      return result
    },
    crc32(bytes) {
      let crc = 0xffffffff
      for (let index = 0; index < bytes.length; index++) {
        crc = crcTable[(crc ^ bytes[index]) & 0xff] ^ (crc >>> 8)
      }
      return (crc ^ 0xffffffff) >>> 0
    },
    timestamp() {
      const date = new Date()
      const pad = value => String(value).padStart(2, '0')
      return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`
    },
    toRmbUpper(value) {
      const amount = this.roundMoney(value)
      if (amount === 0) {
        return '零元整'
      }
      if (Math.abs(amount) >= 10000000000000000) {
        return '金额过大'
      }
      const prefix = amount < 0 ? '负' : ''
      const cents = Math.round(Math.abs(amount) * 100)
      const integer = Math.floor(cents / 100)
      const decimal = cents % 100
      const jiao = Math.floor(decimal / 10)
      const fen = decimal % 10
      let text = `${this.integerToUpper(integer)}元`
      if (decimal === 0) {
        text += '整'
      } else {
        if (jiao > 0) {
          text += `${upperDigits[jiao]}角`
        }
        if (fen > 0) {
          text += `${jiao === 0 ? '零' : ''}${upperDigits[fen]}分`
        }
      }
      return `${prefix}${text}`
    },
    integerToUpper(integer) {
      if (integer === 0) {
        return '零'
      }
      const sections = []
      let rest = integer
      while (rest > 0) {
        sections.push(rest % 10000)
        rest = Math.floor(rest / 10000)
      }
      let text = ''
      let zeroPending = false
      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i]
        if (section === 0) {
          zeroPending = text.length > 0
          continue
        }
        if (text && (zeroPending || section < 1000)) {
          text += '零'
        }
        text += `${this.sectionToUpper(section)}${sectionUnits[i]}`
        zeroPending = false
      }
      return text
    },
    sectionToUpper(section) {
      const divisors = [1000, 100, 10, 1]
      let text = ''
      let zeroPending = false
      let started = false
      divisors.forEach((divisor, index) => {
        const digit = Math.floor(section / divisor) % 10
        if (digit === 0) {
          zeroPending = started
          return
        }
        if (zeroPending) {
          text += '零'
          zeroPending = false
        }
        text += `${upperDigits[digit]}${smallUnits[index]}`
        started = true
      })
      return text
    }
  }
}
</script>

<style lang="scss" scoped>
.toolbox-page {
  background: #f5f7fb;
  min-height: calc(100vh - 84px);
}

.toolbox-layout {
  display: flex;
  gap: 14px;
  min-height: calc(100vh - 124px);
}

.toolbox-nav {
  width: 260px;
  flex: 0 0 260px;
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  padding: 14px 12px;
}

.toolbox-nav-title {
  display: flex;
  align-items: center;
  height: 34px;
  color: #303133;
  font-weight: 600;
  border-bottom: 1px solid #ebeef5;
  margin-bottom: 12px;

  .toolbox-title-symbol {
    display: grid;
    grid-template-columns: repeat(3, 3px);
    grid-auto-rows: 3px;
    gap: 3px;
    width: 18px;
    height: 18px;
    margin-right: 8px;
    align-content: center;
    justify-content: center;

    span {
      width: 3px;
      height: 3px;
      border-radius: 1px;
      background: #409eff;
    }
  }
}

.toolbox-nav-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 10px;
  border-radius: 4px;
  color: #606266;
  cursor: pointer;

  i {
    color: #409eff;
    line-height: 20px;
    margin-top: 1px;
  }

  strong {
    display: block;
    color: #303133;
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
  }

  span {
    display: block;
    color: #909399;
    font-size: 12px;
    line-height: 18px;
    margin-top: 2px;
  }

  &:hover,
  &.active {
    background: #ecf5ff;
  }
}

.toolbox-content {
  flex: 1;
  min-width: 0;
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  padding: 18px;
}

.tool-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-bottom: 14px;
  border-bottom: 1px solid #ebeef5;

  h2 {
    margin: 0;
    color: #303133;
    font-size: 18px;
    line-height: 26px;
    font-weight: 600;
  }

  p {
    margin: 4px 0 0;
    color: #909399;
    font-size: 13px;
    line-height: 18px;
  }
}

.tool-actions {
  white-space: nowrap;
}

.calculator-form {
  margin-top: 18px;

  ::v-deep .el-input-number {
    width: calc(100% - 24px);
  }
}

.input-suffix {
  display: inline-block;
  width: 20px;
  margin-left: 4px;
  color: #606266;
}

.rate-total {
  display: inline-block;
  min-width: 86px;
  height: 32px;
  line-height: 32px;
  color: #67c23a;
  font-weight: 600;

  &.warning {
    color: #e6a23c;
  }
}

.summary-strip {
  display: grid;
  grid-template-columns: repeat(4, minmax(140px, 1fr));
  border: 1px solid #ebeef5;
  border-radius: 4px;
  margin: 4px 0 16px;
  background: #fafafa;
}

.summary-item {
  padding: 14px 16px;
  border-right: 1px solid #ebeef5;

  &:last-child {
    border-right: none;
  }

  span {
    display: block;
    color: #909399;
    font-size: 13px;
    line-height: 18px;
  }

  strong {
    display: block;
    margin-top: 6px;
    color: #303133;
    font-size: 18px;
    line-height: 24px;
    font-weight: 600;

    &.warning {
      color: #e6a23c;
    }
  }
}

.result-table {
  margin-top: 8px;
}

.payment-warning {
  margin-top: 14px;
}

@media (max-width: 960px) {
  .toolbox-layout {
    display: block;
  }

  .toolbox-nav {
    width: auto;
    margin-bottom: 12px;
  }

  .summary-strip {
    grid-template-columns: repeat(2, minmax(140px, 1fr));
  }
}

@media (max-width: 640px) {
  .tool-header {
    display: block;
  }

  .tool-actions {
    margin-top: 12px;
  }

  .summary-strip {
    grid-template-columns: 1fr;
  }

  .summary-item {
    border-right: none;
    border-bottom: 1px solid #ebeef5;

    &:last-child {
      border-bottom: none;
    }
  }
}
</style>
